import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { Dictionary } from 'lodash';
import * as yup from 'yup';
import { applicationContext } from "helpers/services";

interface ITicketCategoriesContext {
    recordCount: number
    search: Search
    categories: ITicketCategory[]
    isLoading: boolean

    updateSearch: (newSearch: Search) => void
    doSearch: (search: Search) => void,
    handleChangePage: (event: any, page: number) => void
    deleteTicketCategory: (categoryId: string) => Promise<void>
}

export interface ITicketCategory {
    ticketCategoryId: string
    color: string
    defaultNotification: string
    name: Dictionary<string>
    fromCustomer: boolean
}

export let ticketCategoriesContext = React.createContext<ITicketCategoriesContext>({} as ITicketCategoriesContext);

let { Provider } = ticketCategoriesContext;


export let searchSchema = yup.object().shape({
    searchText: yup.string().default(''),
    pageNr: yup.number().default(0),
    pageSize: yup.number().default(15)
})

type Search = yup.InferType<typeof searchSchema>;

export let TicketCategoriesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let history = useHistory();
    let location = useLocation();

    let appService = useContext(applicationContext);

    let [isLoading, setLoading] = useState(true);
    let [categories, setCategories] = useState<ITicketCategory[]>([]);
    let [recordCount, setTotalRecords] = useState(0);
    let [search, setSearch] = useState<Search>(searchSchema.getDefault());

    useEffect(() => {
        if (location.state)
            processState(location.state);

    }, [location.state])


    if (!location.state)
        return <Redirect to={{ pathname: location.pathname, state: { search } }} />


    let processState = (state: any) => {
        if (state.search) {
            let request = {
                ...state.search
            };

            setSearch(request);
            searchTicketCategories(request);
        }
    }

    let doSearch = (values: Search) => {
        let request = {
            ...values,
            pageNr: values.pageNr > 0 ? 0 : values.pageNr
        }

        history.push(location.pathname, { search: request });
    }

    let updateSearch = (newSearch: Search) => {
        setSearch({
            ...search,
            ...newSearch
        })
    }

    let searchTicketCategories = async (searchRequest: Search) => {
        try {
            setLoading(true);

            let response = await axios.post('/api/TicketCategory/SearchTicketCategories', searchRequest);
            setCategories(response.data.items);
            setTotalRecords(response.data.recordCount);

        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let handleChangePage = (event: any, page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        history.push(location.pathname, { search: request });
    }

    let deleteTicketCategory = async (categoryId: string) => {
        if (window.confirm("Are you sure that you want to remove this ticket?")) {
            try {
                await axios.delete(`/api/TicketCategory/DeleteTicketCategory/${categoryId}`);
                setCategories(categories.filter(category => category.ticketCategoryId !== categoryId));
                
                await appService.getSimecReferenceData();
                enqueueSnackbar("Ticket deleted", { variant: 'success' });

            } catch (err) { }
        }
    }


    return (
        <Provider value={{
            recordCount,
            search,
            categories,
            isLoading,

            updateSearch,
            doSearch,
            handleChangePage,
            deleteTicketCategory
        }}>
            {children}
        </Provider>
    );
};